/* eslint-disable  @typescript-eslint/await-thenable */
import { Provider } from "mobx-react"
import { NextPageContext } from "next"
import NextApp, { AppProps as NextAppProps, AppContext } from "next/app"
import Head from "next/head"
import { AppModels, modelsInit } from "@models"
import { ThemeProvider } from "@features/common"
import { ModalContainer } from "@lib/modals"
import { isServer, redirect } from "@lib/isomorphic"
import { isExistLangInUri, getLangFromUri } from "@lib/history"
import { DEFAULT_DATING_LANG, DEFAULT_LANG } from "@providers"
import { GlobalStyles } from "styles/globalCss"

interface AppProps extends NextAppProps {
  modelsSsr: AppModels
}

export default class App extends NextApp<AppProps> {
  store: AppModels | null = !isServer() ? modelsInit() : null

  static async getInitialProps(appContext: AppContext) {
    const appProps = await NextApp.getInitialProps(appContext)

    if (!isServer()) return appProps

    const modelsSsr = modelsInit()
    if (appContext.ctx.req) {
      try {
        modelsSsr.GlobalModel.setFirstServerUrl(appContext.ctx)
        await modelsSsr.GlobalModel.getInitial()
        modelsSsr.ResponsiveModel.setUserAgent(
          appContext.ctx.req.headers["user-agent"] ?? "",
        )
        modelsSsr.GlobalModel.initSmartBanner(appContext.ctx)
        // @ts-ignore
        appContext.ctx.req["Models"] = modelsSsr
      } catch {
      } finally {
        // @ts-ignore
        appContext.ctx.req["Models"] = modelsSsr
      }

      if (isServer()) {
        if (appContext.ctx.req.url && /not-found/i.test(appContext.ctx.req.url))
          return appProps

        if (
          await App.statusCodeRedirect({
            statusCode: appContext.ctx.res?.statusCode ?? 0,
            ctx: appContext.ctx,
            models: modelsSsr,
          })
        ) {
          return appProps
        }
        if (
          await App.pathRedirect({
            path: appContext.ctx.req.url,
            ctx: appContext.ctx,
            models: modelsSsr,
          })
        )
          return appProps
      }
    }

    return appProps
  }

  static getDefaultLang(ctx: NextPageContext, isPureDating: boolean) {
    return isExistLangInUri(ctx.req?.url ?? "")
      ? getLangFromUri(ctx.req?.url ?? "")
      : isPureDating
      ? DEFAULT_DATING_LANG
      : DEFAULT_LANG
  }

  static async statusCodeRedirect({
    statusCode,
    ctx,
    models,
  }: {
    statusCode: number
    ctx: NextPageContext
    models: AppModels
  }) {
    switch (statusCode) {
      case 404:
        if (/not-found/i.test(ctx.req?.url ?? "")) return false
        const currentLang = App.getDefaultLang(
          ctx,
          models.GlobalModel.isPureDating,
        )
        redirect(`/${currentLang}/not-found`, ctx)
        return true
    }
    return false
  }

  static async pathRedirect({
    path,
    ctx,
    models,
  }: {
    path: string | undefined
    ctx: NextPageContext
    models: AppModels
  }) {
    if (!path) return false

    if (/\/about-us/i.test(path) && !models.GlobalModel.pages.aboutUs.enabled) {
      const currentLang = App.getDefaultLang(
        ctx,
        models.GlobalModel.isPureDating,
      )
      redirect(`/${currentLang}/not-found`, ctx)
      return true
    }

    const redirectData = models.GlobalModel.redirects.find((redirect) =>
      path.match(redirect.from),
    )

    if (redirectData) {
      redirect(redirectData.to, ctx)
      return true
    }

    return false
  }

  componentDidUpdate() {
    this.store?.hydrate(this.props.pageProps)
  }

  async componentDidMount() {
    this.store?.ResponsiveModel.postInit()
    this.store?.GlobalModel.postInit()
    this.store?.AnalyticsModel.postInit()
  }

  render() {
    const { Component, pageProps } = this.props
    const store = !isServer() ? this.store : modelsInit()

    if (isServer()) store?.hydrate(pageProps)
    else if (!store?.hydrated) store?.hydrate(pageProps)

    return (
      <>
        <Head>
          <meta
            name="viewport"
            content="width=device-width, initial-scale=1 maximum-scale=1"
          />
        </Head>
        <Provider {...store}>
          <ThemeProvider>
            <GlobalStyles />
            <Component />
            <ModalContainer />
          </ThemeProvider>
        </Provider>
      </>
    )
  }
}
